/**
 * Toast notifications.
 *
 * Capped and self-expiring. The cap matters: several agents fire a toast per
 * save, and an uncapped stack covers the screen it is reporting on.
 *
 * IDS ARE LOCAL RATHER THAN `uid()`, AND THAT IS A FIX, NOT A PREFERENCE.
 * `uid()` carries a `resetUid()` test seam, and `vitest.setup.ts` calls it after
 * EVERY test. A toast lives 4200ms; a test finishes in well under one. So a
 * toast pushed by one test was still in this store when the counter reset, and
 * the next test's first toast came back carrying an id already on screen —
 * React then warned about duplicate keys, which it says may drop or duplicate a
 * row. Five tests in the smoke suite were emitting it.
 *
 * It could not happen in the browser, because nothing there calls `resetUid()`.
 * The test seam manufactured the collision it exists to prevent. A toast id is
 * internal identity — nothing asserts on it and nothing displays it — so it has
 * no reason to be resettable, and keeping the counter here means no seam can
 * reach it.
 */

import { create } from 'zustand';

export interface Toast {
  readonly id: string;
  readonly title: string;
  readonly description?: string;
}

/** Oldest toasts are dropped beyond this many. */
const MAX_VISIBLE = 4;
const LIFETIME_MS = 4200;

/** Monotonic for the life of the page, and deliberately not resettable. */
let sequence = 0;

function nextId(): string {
  sequence += 1;
  return `toast-${sequence}`;
}

interface ToastState {
  readonly toasts: readonly Toast[];
  push: (title: string, description?: string) => void;
  dismiss: (id: string) => void;
  clear: () => void;
}

export const useToastStore = create<ToastState>()((set) => ({
  toasts: [],

  push: (title, description) => {
    const id = nextId();
    set((s) => ({
      toasts: [
        ...s.toasts,
        { id, title, ...(description === undefined ? {} : { description }) },
      ].slice(-MAX_VISIBLE),
    }));
    // Self-expiry. Not cleared on unmount by design — the store outlives any
    // single screen and the timer only removes an entry that may already be gone.
    setTimeout(() => {
      set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }));
    }, LIFETIME_MS);
  },

  dismiss: (id) => {
    set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }));
  },

  clear: () => {
    set({ toasts: [] });
  },
}));

/** Imperative helper for use outside components (exports, domain callbacks). */
export const toast = (title: string, description?: string): void => {
  useToastStore.getState().push(title, description);
};
